import { useState, useEffect, useRef } from "react";
import { DAY_COLORS } from '../constants/colors';

// XSS 방지: HTML 텍스트/속성 이스케이프 & 안전한 URL 검증
const escHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// img.src 주입 방지: http(s) 프로토콜만 허용, 내부 quote 제거
const safeImgUrl = (u) => {
  if (!u || typeof u !== 'string') return '';
  const trimmed = u.trim();
  // javascript:, data:, vbscript: 등 위험한 프로토콜 차단
  if (!/^https?:\/\//i.test(trimmed)) return '';
  // URL 내부 따옴표/공백/< > 제거 (속성 탈출 방지)
  if (/["'<>\s]/.test(trimmed)) return '';
  // 길이 제한 (DoS 방지)
  if (trimmed.length > 2048) return '';
  return trimmed;
};

export default function MapView({ spots, destination, onSpotSelect, highlightIdx, darkMode=false, tourPins=[] }) {
  const dm = darkMode;
  const mapContainerRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markersRef = useRef([]);
  const [selectedSpot, setSelectedSpot] = useState(highlightIdx ?? 0);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(false);
  const validSpots = spots
    .filter(s =>
      s && typeof s.lat === "number" && typeof s.lng === "number" &&
      isFinite(s.lat) && isFinite(s.lng) &&
      Math.abs(s.lat) <= 90 && Math.abs(s.lng) <= 180 &&
      !(s.lat === 0 && s.lng === 0)
    )
    .map((s, idx) => ({
      ...s,
      order:    s.order    ?? idx + 1,
      dayIndex: s.dayIndex ?? 0,
      dayLabel: s.dayLabel || `Day ${(s.dayIndex ?? 0) + 1}`,
      name:     s.name     || `장소 ${idx + 1}`,
    }));

  const googleMapsUrl = validSpots.length
    ? `https://www.google.com/maps/dir/${validSpots.map(s=>`${s.lat},${s.lng}`).join('/')}`
    : `https://www.google.com/maps/search/${encodeURIComponent(destination)}`;

  const handleSelect = (idx) => {
    setSelectedSpot(idx);
    onSpotSelect && onSpotSelect(idx);
  };

  useEffect(() => {
    let mounted = true;

    const initMap = async () => {
      try {
        if (!document.getElementById('leaflet-css')) {
          const link = document.createElement('link');
          link.id = 'leaflet-css';
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        }

        if (!window.L) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!mounted || !mapContainerRef.current) return;

        const L = window.L;

        if (leafletMapRef.current) {
          leafletMapRef.current.remove();
          leafletMapRef.current = null;
        }

        let center, zoom;
        if (validSpots.length > 0) {
          const avgLat = validSpots.reduce((s,p) => s+p.lat, 0) / validSpots.length;
          const avgLng = validSpots.reduce((s,p) => s+p.lng, 0) / validSpots.length;
          center = [avgLat, avgLng];
          zoom = validSpots.length === 1 ? 13 : 11;
        } else {
          center = [37.5665, 126.9780];
          zoom = 10;
        }

        const map = L.map(mapContainerRef.current, {
          center, zoom,
          zoomControl: true,
          attributionControl: true,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '\u00a9 OpenStreetMap contributors'
        }).addTo(map);

        leafletMapRef.current = map;
        markersRef.current = [];

        validSpots.forEach((spot, idx) => {
          const color = DAY_COLORS[spot.dayIndex % DAY_COLORS.length] || "#F5A623";
          const isActive = idx === (highlightIdx ?? 0);
          const size = isActive ? 34 : 28;
          const icon = L.divIcon({
            className: '',
            html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2.5px solid white;display:flex;align-items:center;justify-content:center;font-size:${isActive?13:11}px;font-weight:900;color:#0F172A;box-shadow:0 3px 10px rgba(0,0,0,0.35),0 0 0 ${isActive?3:0}px ${color}55;cursor:pointer;transition:all 0.2s">${spot.order ?? idx + 1}</div>`,
            iconSize: [size, size],
            iconAnchor: [size/2, size/2]
          });

          const marker = L.marker([spot.lat, spot.lng], { icon })
            .addTo(map)
            .bindPopup(`
              <div style="font-family:sans-serif;min-width:140px">
                <b style="font-size:13px;color:#0F172A">${escHtml(spot.name)}</b><br/>
                <span style="font-size:11px;color:#666">${escHtml(spot.dayLabel||'')}</span>
                ${spot.description ? `<br/><span style="font-size:11px;color:#888">${escHtml(spot.description)}</span>` : ''}
              </div>
            `, { maxWidth: 200 });

          marker.on('click', () => {
            handleSelect(idx);
          });

          markersRef.current.push(marker);
        });

        if (tourPins && tourPins.length > 0) {
          tourPins.forEach((pin) => {
            const safeTitle = escHtml(pin.title);
            const safeAddr  = escHtml(pin.addr || '');
            const safeImg   = safeImgUrl(pin.img);
            const pinIcon = L.divIcon({
              className: '',
              html: `<div style="width:26px;height:26px;border-radius:50%;background:#2A9D8F;border:2.5px solid white;display:flex;align-items:center;justify-content:center;font-size:13px;box-shadow:0 3px 10px rgba(0,0,0,0.3);cursor:pointer" title="${safeTitle}">🏛️</div>`,
              iconSize: [26, 26],
              iconAnchor: [13, 13]
            });
            L.marker([pin.lat, pin.lng], { icon: pinIcon })
              .addTo(map)
              .bindPopup(`
                <div style="font-family:sans-serif;min-width:140px">
                  <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
                    <span style="background:#2A9D8F;color:#fff;font-size:9px;font-weight:700;padding:2px 6px;border-radius:100px">경남 관광지</span>
                  </div>
                  <b style="font-size:13px;color:#0F172A">${safeTitle}</b>
                  ${safeAddr ? `<br/><span style="font-size:11px;color:#666">📍 ${safeAddr}</span>` : ''}
                  ${safeImg ? `<br/><img src="${safeImg}" style="width:100%;border-radius:6px;margin-top:6px" onerror="this.style.display='none'"/>` : ''}
                </div>
              `, { maxWidth: 200 });
          });
        }

        if (validSpots.length > 1) {
          const latlngs = validSpots.map(s => [s.lat, s.lng]);
          L.polyline(latlngs, {
            color: '#F5A623',
            weight: 2.5,
            opacity: 0.75,
            dashArray: '8, 6'
          }).addTo(map);

          const bounds = L.latLngBounds(latlngs);
          map.fitBounds(bounds, { padding: [40, 40] });
        }

        if (mounted) setMapReady(true);

      } catch(e) {
        console.error('Leaflet load error:', e);
        if (mounted) setMapError(true);
      }
    };

    const timer = setTimeout(initMap, 100);

    return () => {
      mounted = false;
      clearTimeout(timer);
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, [JSON.stringify(spots.map(s => s.lat + ',' + s.lng))]); // eslint-disable-line

  useEffect(() => {
    if (!leafletMapRef.current || !markersRef.current.length) return;
    const marker = markersRef.current[highlightIdx];
    if (marker) {
      marker.openPopup();
      const spot = validSpots[highlightIdx];
      if (spot) leafletMapRef.current.panTo([spot.lat, spot.lng]);
    }
  }, [highlightIdx]); // eslint-disable-line

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

      {/* 지도 컨테이너 */}
      <div style={{ position:"relative", borderRadius:14, overflow:"hidden", border:`1px solid ${dm?"rgba(129,140,248,0.2)":"rgba(99,102,241,0.2)"}` }}>
        <div
          ref={mapContainerRef}
          style={{ width:"100%", height:340, background:dm?"#0D1822":"#E8E4D8" }}
        />

        {!mapReady && !mapError && (
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:dm?"rgba(10,22,40,0.85)":"rgba(240,236,228,0.85)", backdropFilter:"blur(4px)", borderRadius:14 }}>
            <div style={{ width:48, height:48, border:`3px solid ${dm?"rgba(129,140,248,0.2)":"rgba(99,102,241,0.15)"}`, borderTopColor:"var(--accent)", borderRadius:"50%", animation:"spin 1s linear infinite", marginBottom:12 }}/>
            <p style={{ color:"var(--accent)", fontWeight:700, fontSize:13 }}>지도 로딩 중...</p>
          </div>
        )}

        {mapError && (
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:dm?"rgba(10,22,40,0.9)":"rgba(240,236,228,0.9)", borderRadius:14 }}>
            <div style={{ fontSize:32, marginBottom:10 }}>🗺️</div>
            <p style={{ color:"var(--accent)", fontWeight:700, fontSize:13, marginBottom:6 }}>지도를 불러올 수 없습니다</p>
            <a href={googleMapsUrl} target="_blank" rel="noreferrer"
              style={{ fontSize:12, color:"var(--teal)", fontWeight:700, textDecoration:"none", padding:"7px 16px", border:`1px solid ${dm?"rgba(78,205,196,0.35)":"rgba(42,157,143,0.35)"}`, borderRadius:100 }}>
              Google Maps에서 열기 →
            </a>
          </div>
        )}

        {mapReady && validSpots.length === 0 && (
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", pointerEvents:"none" }}>
            <div style={{ padding:"12px 20px", borderRadius:12, background:dm?"rgba(10,22,40,0.85)":"rgba(255,255,255,0.9)", backdropFilter:"blur(8px)", border:`1px solid ${dm?"rgba(129,140,248,0.2)":"rgba(99,102,241,0.2)"}`, textAlign:"center" }}>
              <p style={{ color:"var(--accent)", fontWeight:700, fontSize:13, marginBottom:4 }}>📍 {destination}</p>
              <p style={{ color:dm?"rgba(226,232,240,0.78)":"rgba(51,65,85,0.72)", fontSize:11 }}>일정을 다시 생성하면 장소 마커가 표시돼요</p>
            </div>
          </div>
        )}
      </div>

      {validSpots.length > 0 && (
        <a href={googleMapsUrl} target="_blank" rel="noreferrer"
          style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"10px", borderRadius:10, background:dm?"rgba(129,140,248,0.06)":"rgba(99,102,241,0.06)", border:dm?"1px solid rgba(129,140,248,0.2)":"1px solid rgba(99,102,241,0.2)", color:"var(--accent)", fontWeight:700, fontSize:13, textDecoration:"none", transition:"all 0.2s" }}
          onMouseEnter={e=>e.currentTarget.style.background=dm?"rgba(129,140,248,0.12)":"rgba(99,102,241,0.1)"}
          onMouseLeave={e=>e.currentTarget.style.background=dm?"rgba(129,140,248,0.06)":"rgba(99,102,241,0.06)"}>
          🌐 Google Maps에서 전체 경로 보기 ({validSpots.length}개 장소)
        </a>
      )}

      {/* 장소 목록 */}
      <div style={{ display:"flex", flexDirection:"column", gap:7, maxHeight:230, overflowY:"auto" }}>
        {validSpots.length === 0 && (
          <div style={{ textAlign:"center", padding:20, color:dm?"rgba(226,232,240,0.68)":"rgba(51,65,85,0.70)", fontSize:13 }}>
            📍 지도에 표시할 장소가 없습니다. 일정을 새로 생성해보세요.
          </div>
        )}
        {validSpots.map((spot, idx) => {
          const color = DAY_COLORS[spot.dayIndex % DAY_COLORS.length] || "#FFD700";
          const isActive = idx === selectedSpot;
          return (
            <div key={idx} onClick={() => handleSelect(idx)}
              style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"10px 13px", borderRadius:11, cursor:"pointer", border:`1.5px solid ${isActive?color+"88":dm?"rgba(129,140,248,0.1)":"rgba(99,102,241,0.12)"}`, background: isActive?`${color}14`:dm?"rgba(255,255,255,0.04)":"rgba(255,255,255,0.75)", transition:"all 0.2s" }}>
              <div style={{ width:26, height:26, minWidth:26, borderRadius:"50%", background:color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:900, color:"#0F172A", flexShrink:0 }}>{spot.order ?? idx + 1}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:2, flexWrap:"wrap" }}>
                  <span style={{ fontSize:13, fontWeight:700, color: isActive?color:"var(--text)" }}>{spot.name}</span>
                  <span style={{ fontSize:10, padding:"2px 7px", borderRadius:100, background:`${color}22`, color, fontWeight:700 }}>{spot.dayLabel}</span>
                </div>
                {spot.description && <div style={{ fontSize:11, color:dm?"rgba(226,232,240,0.68)":"rgba(51,65,85,0.72)", lineHeight:1.4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{spot.description}</div>}
              </div>
              {isActive && <span style={{ fontSize:14, flexShrink:0 }}>📍</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
